// pages/BookingSuccess.js
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircle, 
  Calendar, 
  Clock, 
  MapPin, 
  Shield, 
  Key,
  ArrowRight,
  Home as HomeIcon
} from 'lucide-react';
import '../styles/BookingSuccess.css';

const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { booking, otp, service, bookings, cartItems, isMultipleBooking } = location.state || {};

  // Handle both single and multiple bookings
  if (!booking && !bookings) {
    navigate('/services');
    return null;
  }

  // For multiple bookings from cart
  if (isMultipleBooking && bookings) {
    return (
      <div className="booking-success-page">
        <div className="container">
          <div className="success-content">
            {/* Success Header */}
            <div className="success-header">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h1>All Bookings Confirmed!</h1>
              <p>Your {bookings.length} services have been successfully booked. Our professionals will arrive at your scheduled times.</p>
            </div>

            {/* Multiple Bookings Details */}
            <div className="booking-details">
              <h3>Booking Details</h3>
              
              {bookings.map((booking, index) => (
                <div key={index} className="detail-card">
                  <div className="detail-header">
                    <div className="service-icon">
                      <div className="service-icon-placeholder">
                        {booking.service?.charAt(0) || 'S'}
                      </div>
                    </div>
                    <div>
                      <h4>{booking.service}</h4>
                      <span className="booking-id">Booking ID: #{booking.id?.slice(-8)}</span>
                    </div>
                  </div>
                  
                  <div className="detail-items">
                    <div className="detail-item">
                      <Calendar size={16} />
                      <span>Date: {new Date(booking.date).toLocaleDateString('en-IN')}</span>
                    </div>
                    <div className="detail-item">
                      <Clock size={16} />
                      <span>Time: {booking.timeSlot}</span>
                    </div>
                    <div className="detail-item">
                      <MapPin size={16} />
                      <span>Address: {booking.address?.street}, {booking.address?.city}</span>
                    </div>
                  </div>
                  
                  <div className="price-info">
                    <span>Amount: ₹{booking.servicePrice || booking.price}</span>
                  </div>

                  {booking.otp && (
                    <div className="otp-display-small">
                      <Key size={16} />
                      <span>OTP: {booking.otp}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button 
                className="dashboard-btn"
                onClick={() => navigate('/dashboard')}
              >
                <HomeIcon size={16} />
                Go to Dashboard
              </button>
              <button 
                className="services-btn"
                onClick={() => navigate('/services')}
              >
                Book Another Service
                <ArrowRight size={16} />
              </button>
            </div>

            {/* Support Info */}
            <div className="support-info">
              <p>Need help? Contact our support team at <strong>+91 98765 43210</strong></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Single booking (original logic)
  return (
    <div className="booking-success-page">
      <div className="container">
        <div className="success-content">
          {/* Success Header */}
          <div className="success-header">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h1>Booking Confirmed!</h1>
            <p>Your service has been successfully booked. Our professional will arrive at your scheduled time.</p>
          </div>

          {/* OTP Section */}
          {otp && (
            <div className="otp-section">
              <div className="otp-header">
                <Key size={24} />
                <h2>Service Verification OTP</h2>
              </div>
              <div className="otp-display">
                <span className="otp-code">{otp}</span>
              </div>
              <div className="otp-info">
                <Shield size={16} />
                <p>Share this OTP with the service professional when they arrive to start the work. This ensures verified service delivery.</p>
              </div>
            </div>
          )}

          {/* Booking Details */}
          <div className="booking-details">
            <h3>Booking Details</h3>
            
            <div className="detail-card">
              <div className="detail-header">
                <div className="service-icon">
                  <div className="service-icon-placeholder">
                    {service?.name?.charAt(0) || booking?.service?.charAt(0) || 'S'}
                  </div>
                </div>
                <div>
                  <h4>{service?.name || booking?.service}</h4>
                  <span className="booking-id">Booking ID: #{booking?.id?.slice(-8)}</span>
                </div>
              </div>
              
              <div className="detail-items">
                <div className="detail-item">
                  <Calendar size={16} />
                  <span>Date: {new Date(booking?.date).toLocaleDateString('en-IN')}</span>
                </div>
                <div className="detail-item">
                  <Clock size={16} />
                  <span>Time: {booking?.timeSlot}</span>
                </div>
                <div className="detail-item">
                  <MapPin size={16} />
                  <span>Address: {booking?.address?.street}, {booking?.address?.city}</span>
                </div>
              </div>
              
              <div className="price-info">
                <span>Total Amount: ₹{booking?.servicePrice || booking?.price}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button 
              className="dashboard-btn"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon size={16} />
              Go to Dashboard
            </button>
            <button 
              className="services-btn"
              onClick={() => navigate('/services')}
            >
              Book Another Service
              <ArrowRight size={16} />
            </button>
          </div>

          {/* Support Info */}
          <div className="support-info">
            <p>Need help? Contact our support team at <strong>+91 98765 43210</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;